// 原価合計グリッド専用の高さ計算・行正規化・列定義をまとめる。
export function createGenkaSummaryGridService(deps) {
    const {
        PANEL_TYPE,
        entrydataRef,
        ensureTotalDataRef,
        isEmpty,
        DicVal,
        findColumnInfoByCandidates,
        genkaTradeConditionColInfo,
        genkaSummaryColInfoByField,
        getGenkaTradeConditionDataId,
        currentGenkaPatternNo,
        resolveGenkaSummaryItem,
        resolveGenkaSummaryLabel,
        getGenkaSummaryOrder,
        emptyGenkaSummaryRow,
        getGenkaSummaryGridDisplayValue,
        setGenkaSummaryGridValue,
        formatGenkaSummaryDisplayValue,
        isGenkaOverseasEntry,
        normalizeColumnIdUpper,
        normalizeSearchParamObject
    } = deps;

    // 初期化順の影響を避けるため、const 値は必要時に getter 経由で参照します。
    function currentGenkaTradeConditionDataId() {
        if (typeof getGenkaTradeConditionDataId === "function") {
            return String(getGenkaTradeConditionDataId() ?? "").trim();
        }
        return "";
    }

    // 原価サマリの項目順も遅延取得し、component 側の定数初期化完了後に使います。
    function currentGenkaSummaryOrder() {
        if (typeof getGenkaSummaryOrder === "function") {
            const value = getGenkaSummaryOrder();
            return Array.isArray(value) ? value : [];
        }
        return [];
    }

    // 原価右合計グリッドも全行表示に合わせる。
    function genkaSummaryFixedHeight(totalPanel) {
        const rows = ensureTotalDataRef(totalPanel).current;
        const rowCount = Array.isArray(rows) ? rows.length : 0;
        const calc = (rowCount * 28) + 56;
        const h = Math.max(300, calc);
        return h + "px";
    }

    // 原価合計が未返却時に使う仮行（仕様書準拠の項目順）。
    function defaultGenkaSummaryRows() {
        return currentGenkaSummaryOrder().map((itemId, idx) => ({ ...emptyGenkaSummaryRow(itemId, idx), __displayOnly: true }));
    }

    // 原価合計を仕様固定順（genkaSummaryOrder）へ正規化する。
    // 返却順が前後しても、欠損があっても、右側は常に同じ行順で描画する。
    function normalizeGenkaSummaryRows(rows) {
        const baseRows = defaultGenkaSummaryRows();
        if (!Array.isArray(rows) || rows.length === 0) return baseRows;

        const baseMap = {};
        baseRows.forEach((r) => {
            const id = resolveGenkaSummaryItem(DicVal(r, "total_item", ""));
            if (id === "") return;
            baseMap[id] = { ...r, total_item: id, total_key: id, total_label: resolveGenkaSummaryLabel(id) };
        });

        rows.forEach((r) => {
            const rawItem = String(DicVal(r, "total_item", "")).trim();
            const key = resolveGenkaSummaryItem(DicVal(r, "total_key", "") || rawItem);
            if (isEmpty(key) || !(key in baseMap)) return;
            const next = {
                ...baseMap[key],
                ...r,
                total_item: key,
                total_key: key,
                total_label: resolveGenkaSummaryLabel(key)
            };
            // total_item が value2 側Column IDで返る場合、値を total_value2 側へ寄せる。
            const sourceIdUpper = normalizeColumnIdUpper(DicVal(r, "total_key", "") || rawItem);
            if (sourceIdUpper === "SSDGM_FOBKINF") {
                const v1 = String(DicVal(r, "total_value", "") ?? "").trim();
                const v2 = String(DicVal(r, "total_value2", "") ?? "").trim();
                if (v2 === "" && v1 !== "") next.total_value2 = v1;
            }
            baseMap[key] = next;
        });

        return currentGenkaSummaryOrder().map((itemId, idx) => ({
            ...baseMap[itemId],
            rowid: String(idx)
        }));
    }

    function buildGenkaTotalGridColumns() {
        const genkaTradeConditionDataId = currentGenkaTradeConditionDataId();
        const tradeConditionBaseColInfo = genkaTradeConditionColInfo(
            findColumnInfoByCandidates(["SSDGM_BOUEKIJYOKEN"], [PANEL_TYPE.GENKA_SUMMARY, "127", "t2"])
        );
        const tradeConditionColProps = {
            datatype: String(DicVal(tradeConditionBaseColInfo, "datatype", "6") ?? "6"),
            dataid: String(DicVal(tradeConditionBaseColInfo, "dataid", genkaTradeConditionDataId) ?? genkaTradeConditionDataId),
            dataidctrl: String(DicVal(tradeConditionBaseColInfo, "dataidctrl", "m_code") ?? "m_code"),
            mastertype: String(DicVal(tradeConditionBaseColInfo, "mastertype", genkaTradeConditionDataId) ?? genkaTradeConditionDataId),
            dbcolumn: String(DicVal(tradeConditionBaseColInfo, "dbcolumn", "m_code_name") ?? "m_code_name"),
            dbcolumnnm: String(DicVal(tradeConditionBaseColInfo, "dbcolumnnm", DicVal(tradeConditionBaseColInfo, "dbcolumn", "m_code_name")) ?? "m_code_name"),
            keyinfo: String(DicVal(tradeConditionBaseColInfo, "keyinfo", "") ?? "").trim(),
            searchtype: String(DicVal(tradeConditionBaseColInfo, "searchtype", "") ?? "").trim(),
            searchparam: normalizeSearchParamObject(DicVal(tradeConditionBaseColInfo, "searchparam", null)),
            suppressClickEdit: false
        };
        const fixed = [
            { colId: "total_label", field: "total_label", headerName: "", colinfo: genkaSummaryColInfoByField("total_item") },
            {
                colId: "SSDGM_BOUEKIJYOKEN",
                field: "total_value",
                headerName: "",
                colinfo: tradeConditionBaseColInfo,
                ...tradeConditionColProps
            },
            {
                colId: "SSDGM_FOBKINF",
                field: "total_value2",
                headerName: "",
                colinfo: genkaSummaryColInfoByField("total_value2"),
                datatype: String(DicVal(genkaSummaryColInfoByField("total_value2"), "datatype", "1") ?? "1"),
                suppressClickEdit: false
            }
        ];
        const isGenkaTradeConditionEditablePattern = (patternNo) => {
            const no = Number(patternNo);
            if (!Number.isFinite(no)) return false;
            return (no >= 1 && no <= 3) || (no >= 4 && no <= 9);
        };
        const isGenkaTradeConditionEditable = (patternNo) => {
            return isGenkaOverseasEntry(entrydataRef.current) || isGenkaTradeConditionEditablePattern(patternNo);
        };
        return fixed.map((col) => {
            const colId = String(col.colId ?? "");
            const isLabel = colId === "total_label";
            const isValue1 = colId === "SSDGM_BOUEKIJYOKEN" || String(col.field ?? "") === "total_value";
            const isValue2 = colId === "SSDGM_FOBKINF" || String(col.field ?? "") === "total_value2";
            return {
                ...col,
                suppressMovable: true,
                lockPosition: true,
                sortable: false,
                resizable: false,
                suppressMenu: true,
                editable: (params) => {
                    const patternNo = currentGenkaPatternNo();
                    if (!Number.isFinite(Number(patternNo)) || Number(patternNo) < 1 || Number(patternNo) > 9) {
                        return false;
                    }
                    const rowId = resolveGenkaSummaryItem(DicVal(DicVal(params, "data", {}), "total_item", ""));
                    const isTradeConditionRow = rowId === "SSDGM_BOUEKIJYOKEN";
                    if (!isTradeConditionRow) return false;
                    if (!isGenkaTradeConditionEditable(patternNo)) return false;
                    return isValue1 || isValue2;
                },
                valueGetter: (params) => {
                    if (isLabel) return String(DicVal(DicVal(params, "data", {}), "total_label", "") ?? "");
                    if (isValue1) return getGenkaSummaryGridDisplayValue(DicVal(params, "data", {}), "total_value");
                    if (isValue2) return getGenkaSummaryGridDisplayValue(DicVal(params, "data", {}), "total_value2");
                    return String(DicVal(params, "value", "") ?? "");
                },
                valueSetter: (params) => {
                    if (isLabel) return false;
                    if (isValue1) return setGenkaSummaryGridValue(DicVal(params, "data", {}), "total_value", DicVal(params, "newValue", ""));
                    if (isValue2) return setGenkaSummaryGridValue(DicVal(params, "data", {}), "total_value2", DicVal(params, "newValue", ""));
                    return false;
                },
                valueFormatter: (params) => {
                    if (isLabel) return String(DicVal(params, "value", "") ?? "");
                    if (isValue1) return formatGenkaSummaryDisplayValue(params, false);
                    if (isValue2) return formatGenkaSummaryDisplayValue(params, true);
                    return String(DicVal(params, "value", "") ?? "");
                },
                colSpan: (params) => {
                    if (!isValue1) return 1;
                    const rowId = resolveGenkaSummaryItem(DicVal(DicVal(params, "data", {}), "total_item", ""));
                    return rowId === "SSDGM_BOUEKIJYOKEN" ? 1 : 2;
                },
                flex: isLabel ? 1.9 : 1,
                minWidth: isLabel ? 148 : 60,
                cellStyle: (params) => {
                    const rowId = resolveGenkaSummaryItem(DicVal(DicVal(params, "data", {}), "total_item", ""));
                    const isTradeConditionRow = rowId === "SSDGM_BOUEKIJYOKEN";
                    const patternNo = currentGenkaPatternNo();
                    const editableTradeCondition = isTradeConditionRow && isGenkaTradeConditionEditable(patternNo);
                    if (isLabel) {
                        return { backgroundColor: "#8fa4a3", color: "#ffffff", fontWeight: 600, textAlign: "left", borderColor: "#d3d7da" };
                    }
                    if (isValue1) {
                        return { backgroundColor: editableTradeCondition ? "#ffffff" : "#eef1f1", color: "#2f3a4a", textAlign: "right", borderColor: "#d3d7da" };
                    }
                    if (isValue2) {
                        return { backgroundColor: editableTradeCondition ? "#ffffff" : "#eef1f1", color: "#2f3a4a", textAlign: "right", borderColor: "#d3d7da", visibility: isTradeConditionRow ? "visible" : "hidden" };
                    }
                    return { backgroundColor: "#ffffff", color: "#2f3a4a", borderColor: "#d3d7da" };
                }
            };
        });
    }

    function buildTotalGridColumns(section, colDef) {
        if (section.id !== "genka") return colDef;
        return buildGenkaTotalGridColumns();
    }

    return {
        defaultGenkaSummaryRows,
        genkaSummaryFixedHeight,
        normalizeGenkaSummaryRows,
        buildGenkaTotalGridColumns,
        buildTotalGridColumns
    };
}
